"use client";

import { useEffect, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { ArrowLeft, Boxes, ClipboardCopy, ExternalLink, FlaskConical, Plus, RefreshCw, Settings2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { InfrastructureProjectsDirectory } from "./infrastructure-projects-directory";
import { UniversalAiModelPicker } from "./universal-ai-model-picker";
import { UniversalInfrastructureActions, dispatchAddInfrastructure } from "./universal-infrastructure-actions";

type App = { entity_key: string; display_name: string; lifecycle_state: string; health_state: string; attributes?: Record<string, unknown> };
type Assignment = {
  assignment_id: string; application_entity_key: string; target_entity_key: string; assignment_slot: string;
  category_key?: string | null; provider_key?: string | null; assignment_role: string; environment_key: string; state: string;
};
type Release = {
  release_id: string; release_key: string; environment_key: string; version: string; is_current: boolean;
  state: string; health_state: string; source_repository?: string | null; git_commit_sha?: string | null;
  metadata?: Record<string, unknown>;
};
type Resource = { resource_key: string; display_name: string; provider_key?: string | null; identity_email?: string | null };
type BackendService = { backend_service_key: string; display_name: string; provider_key?: string | null; identity_email?: string | null };

function configuredResource(assignment: Assignment, resources: Resource[], services: BackendService[]) {
  const resource = resources.find((item) => assignment.target_entity_key === `RESOURCE:${item.resource_key}`);
  const service = services.find((item) => assignment.target_entity_key === `BACKEND_SERVICE:${item.backend_service_key}`);
  return {
    name: resource?.display_name ?? service?.display_name ?? assignment.target_entity_key,
    provider: resource?.provider_key ?? service?.provider_key ?? assignment.provider_key ?? null,
    email: resource?.identity_email ?? service?.identity_email ?? null,
  };
}

function safeWebUrl(value: unknown): string | null {
  if (typeof value !== "string" || !value.trim()) return null;
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === "https:" && !parsed.username && !parsed.password ? parsed.href : null;
  } catch { return null; }
}

const linkKeys = ["launch_url", "preview_url", "web_url", "application_url", "app_url", "deployment_url", "url"] as const;
function launchTargets(app: App | null, releases: Release[]) {
  const targets: { label: string; url: string; verified: boolean }[] = [];
  const add = (object: Record<string, unknown> | undefined, provenance: string, verified: boolean) => {
    for (const key of linkKeys) {
      const url = safeWebUrl(object?.[key]);
      if (url && !targets.some((item) => item.url === url))
        targets.push({ label: `${provenance} · ${key.replaceAll("_", " ")}`, url, verified });
    }
  };
  for (const release of releases) {
    add(release.metadata, `${release.environment_key} release v${release.version}`,
      release.is_current && release.state === "ACTIVE" && release.health_state === "HEALTHY");
  }
  add(app?.attributes, "Registered application", false);
  return targets;
}

export function AppsConfigurationEngine({
  app, assignments, releases, resources, backendServices, supabase, connected, onClose, onRefresh, onOpenTest,
}: {
  app: App; assignments: Assignment[]; releases: Release[]; resources: Resource[]; backendServices: BackendService[];
  supabase: SupabaseClient | null; connected: boolean; onClose: () => void; onRefresh: () => void | Promise<void>;
  onOpenTest: () => void;
}) {
  const [templateVisible, setTemplateVisible] = useState(false);
  const [message, setMessage] = useState("");
  const template = useMemo(() => JSON.stringify({
    schema: "RUNYI_APP_CONFIGURATION_TEMPLATE_V1",
    application: { entity_key: app.entity_key, display_name: app.display_name, lifecycle_state: app.lifecycle_state },
    resource_assignments: assignments.map((a) => {
      const r = configuredResource(a, resources, backendServices);
      return { category_key: a.category_key ?? a.assignment_slot, target_entity_key: a.target_entity_key,
        display_name: r.name, provider_key: r.provider, environment_key: a.environment_key,
        assignment_role: a.assignment_role, state: a.state };
    }),
    source_and_release: releases.map((r) => ({ release_key: r.release_key, environment_key: r.environment_key,
      version: r.version, state: r.state, health_state: r.health_state, is_current: r.is_current,
      source_repository: r.source_repository, git_commit_sha: r.git_commit_sha })),
    note: "Authorized development specification, not credentials or permission to execute. Linked project revisions are available from the project configuration workspace.",
  }, null, 2), [app, assignments, resources, backendServices, releases]);
  async function copyTemplate() {
    try {
      await navigator.clipboard.writeText(template);
      setMessage("Application specification copied. Share only with authorized developers.");
    } catch { setMessage("Select and copy the specification below. Clipboard access is unavailable in this runtime."); }
  }
  return <div className="fixed inset-0 z-[125] flex flex-col overflow-hidden bg-background" aria-label={`Configuration engine for ${app.display_name}`}>
    <header className="flex shrink-0 items-center gap-2 border-b border-foreground/10 px-3 py-2">
      <Button size="icon" variant="ghost" className="size-9 shrink-0" onClick={onClose} aria-label="Back to Apps"><ArrowLeft className="size-4" /></Button>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">Workspace Configuration Engine</p>
        <p className="truncate text-[10px] text-foreground/50">{app.display_name}</p>
      </div>
      <Button size="icon" variant="ghost" className="size-9" aria-label="Refresh application configuration" onClick={() => void onRefresh()}><RefreshCw className="size-4" /></Button>
    </header>
    <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain p-3 pb-10">
      <div className="rounded-xl border border-primary/20 bg-primary/[0.04] p-3">
        <p className="text-xs font-semibold">Application / product configuration</p>
        <p className="mt-1 break-all font-mono text-[9px] text-foreground/45">{app.entity_key}</p>
        <p className="mt-2 text-[10px] text-foreground/55">Configure the existing product here. Development and source changes are performed by authorized AI agents or developers using its governed specification.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={onOpenTest}><FlaskConical className="size-3.5" />Open / test app</Button>
          <Button size="sm" variant="outline" onClick={() => setTemplateVisible((value) => !value)}><ClipboardCopy className="size-3.5" />Development template</Button>
        </div>
      </div>

      {templateVisible && <section className="rounded-xl border border-foreground/10 p-3">
        <p className="text-xs font-semibold">Application infrastructure template</p>
        <p className="mt-1 text-[10px] text-foreground/50">Read-only application specification for personal ChatGPT, RUNYI AI and other authorized development systems. Project revisions below provide the full requirements and preferences.</p>
        <Textarea readOnly value={template} className="mt-2 min-h-52 font-mono text-[10px]" aria-label="Application development template" />
        <Button size="sm" variant="outline" className="mt-2" onClick={() => void copyTemplate()}><ClipboardCopy className="size-3.5" />Copy specification</Button>
      </section>}

      <section className="rounded-xl border border-foreground/10 p-3">
        <div className="flex items-center gap-2"><Settings2 className="size-4 text-primary" /><p className="text-xs font-semibold">AI provider & model</p></div>
        <p className="mt-1 text-[10px] text-foreground/50">Choose the application's authorized Marketplace AI provider and model.</p>
        <div className="mt-2"><UniversalAiModelPicker supabase={supabase} connected={connected} applicationEntityKey={app.entity_key} onConfigured={() => void onRefresh()} /></div>
      </section>

      <section className="rounded-xl border border-foreground/10 p-3">
        <div className="flex items-center justify-between gap-2">
          <div><p className="text-xs font-semibold">Infrastructure, products & services</p><p className="text-[10px] text-foreground/50">{assignments.length} registered assignments · all authorized Marketplace categories</p></div>
          <Button size="sm" variant="outline" onClick={() => {
            dispatchAddInfrastructure({ source: "APPLICATION_CONFIGURATION_ENGINE", application_entity_key: app.entity_key,
              application_display_name: app.display_name, environment_key: "PRODUCTION", target_scope: "APPLICATION", mode: "NEW" });
            onClose();
          }}><Plus className="size-3.5" />Add</Button>
        </div>
        <div className="mt-2 space-y-2">
          {assignments.length === 0 && <p className="text-xs text-foreground/50">No assignments yet. Add resources from the scoped Marketplace or create a linked Project below.</p>}
          {assignments.map((a) => {
            const r = configuredResource(a, resources, backendServices);
            return <div key={a.assignment_id} className="rounded-xl border border-foreground/10 bg-foreground/[0.02] p-2.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold">{r.name}</p>
                  <p className="mt-1 text-[10px] text-foreground/55">{a.category_key ?? a.assignment_slot} · {r.provider ?? "Unresolved provider"}</p>
                  <p className="mt-1 break-all text-[10px] text-foreground/45">{r.email ?? "Identity pending"}</p>
                  <p className="mt-1 text-[10px] text-foreground/50">{a.environment_key} · {a.assignment_role} · {a.state}</p>
                </div>
                <UniversalInfrastructureActions supabase={supabase} connected={connected}
                  target={{ target_entity_key: a.target_entity_key, display_name: r.name,
                    category_key: a.category_key ?? a.assignment_slot, provider_key: r.provider,
                    target_kind: a.target_entity_key.startsWith("PROVIDER_CAPABILITY:") ? "CAPABILITY" : "INSTALLED",
                    environment_key: a.environment_key, identity_email: r.email }}
                  applications={[app]} assignments={[a]} onChanged={onRefresh} />
              </div>
            </div>;
          })}
        </div>
        <p className="mt-2 text-[10px] text-foreground/45">The resource ⋮ menu supports activation, standby, disable and removal of the RUNYI assignment. Removing an assignment never deletes a shared provider resource.</p>
      </section>

      <section className="rounded-xl border border-foreground/10 p-2">
        <div className="px-1 pb-2"><p className="text-xs font-semibold">Linked Projects & configuration revisions</p><p className="mt-1 text-[10px] text-foreground/50">Create a new project for this application or edit a linked project's requirements, infrastructure assignments and reusable AI/developer template.</p></div>
        <InfrastructureProjectsDirectory open supabase={supabase} connected={connected} embedded hideSearch
          initialApplicationEntityKey={app.entity_key} onlyApplicationEntityKey={app.entity_key} onOpenTest={() => onOpenTest()} onClose={onClose} />
      </section>
      {message && <p role="status" className="text-xs text-foreground/65">{message}</p>}
    </div>
  </div>;
}

export function AppsOpenTestScreen({ app, releases, projectName, onBack }: {
  app: App | null; releases: Release[]; projectName?: string; onBack: () => void;
}) {
  const name = app?.display_name ?? projectName ?? "Unassigned project";
  const targets = useMemo(() => launchTargets(app, releases), [app, releases]);
  const [tested, setTested] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState("");
  useEffect(() => { setTested({}); setNotes(""); }, [app?.entity_key, projectName]);
  const checks = ["Application opens", "Navigation and visible controls", "Authentication and permissions",
    "Database / backend operations", "Assigned infrastructure and integrations", "Mobile viewport and Back"];
  return <div className="fixed inset-0 z-[130] flex flex-col overflow-hidden bg-background" aria-label={`Open and test ${name}`}>
    <header className="flex shrink-0 items-center gap-2 border-b border-foreground/10 px-3 py-2">
      <Button size="icon" variant="ghost" className="size-9" onClick={onBack} aria-label="Back to application workspace"><ArrowLeft className="size-4" /></Button>
      <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">Open & Test</p><p className="truncate text-[10px] text-foreground/50">{name}</p></div>
      <FlaskConical className="size-4 shrink-0 text-primary" />
    </header>
    <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain p-3 pb-10">
      <section className="rounded-xl border border-foreground/10 p-3">
        <p className="text-xs font-semibold">Registered application runtimes</p>
        <p className="mt-1 text-[10px] text-foreground/50">Only actual HTTPS launch/preview addresses registered in the application or its releases appear. An application release alone is not a launchable URL.</p>
        <div className="mt-3 space-y-2">
          {targets.map((target) => <a key={target.url} href={target.url} target="_blank" rel="noopener noreferrer"
              className="flex min-h-11 items-center justify-between gap-2 rounded-xl border border-primary/20 bg-primary/[0.04] px-3 py-2 text-xs font-semibold">
              <span className="min-w-0"><span className="block truncate">{target.label}</span><span className="mt-1 block truncate font-mono text-[9px] font-normal text-foreground/50">{target.url}</span></span>
              <span className="flex shrink-0 items-center gap-1"><Badge variant="outline" className="text-[9px]">{target.verified ? "HEALTHY RELEASE" : "REGISTERED URL"}</Badge><ExternalLink className="size-4" /></span>
            </a>)}
          {targets.length === 0 && <p className="rounded-xl border border-dashed border-foreground/10 px-3 py-4 text-xs text-foreground/55">No launchable web runtime is registered for this product. Link a verified deployment/preview URL through the governed application release or application registration, then return to Open & Test. RUNYI will not invent a launch URL.</p>}
        </div>
      </section>
      <section className="rounded-xl border border-foreground/10 p-3">
        <p className="text-xs font-semibold">Manual application test checklist</p>
        <p className="mt-1 text-[10px] text-foreground/50">Session-only checks; these do not claim automated verification or change application release health.</p>
        <div className="mt-2 space-y-2">
          {checks.map((check) => <label key={check} className="flex min-h-10 items-center gap-2 rounded-lg bg-foreground/[0.025] px-3 text-xs">
            <input type="checkbox" checked={tested[check] ?? false} onChange={(event) => setTested((value) => ({ ...value, [check]: event.target.checked }))} />
            <span>{check}</span>
          </label>)}
        </div>
        <Textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Test observations or issues for an authorized developer…" className="mt-3 min-h-24 text-xs" />
        <p className="mt-2 text-[10px] text-foreground/45">To correct an issue, open this product's Configuration Engine and share its versioned project specification and test findings with an authorized AI or developer.</p>
      </section>
    </div>
  </div>;
}
